import React, { useCallback, useEffect, useState } from 'react';
import { tourEvent } from '../tour/tourEvents';

interface ApiKey { id: number; name: string; key_prefix: string; created_at: string; last_used_at: string | null }

// The instructions a user pastes into Claude. Kept here so the copy always
// matches the API this build serves.
const claudePrompt = (base: string, key: string) => `You can query my MarketMapr account through its API from your sandbox with curl.

Host: ${base}/api/v1   (add this domain to your network allowlist if needed)
Auth header: X-API-Key: ${key}
All bodies are JSON (Content-Type: application/json).

Start every session with: GET /account — it returns my plan and limits (people per project import, browse depth, contact credits). Plan around those limits; tell me before anything would exceed them.

Endpoints:
- POST /people/count      {"filters": {...}}  → total matches (free, use this for "how many" questions)
- POST /people/search     {"filters": {...}, "limit": 10}  → up to 25 example people (free; NOT a random sample — never use for statistics)
- POST /people/breakdown  {"filters": {...}, "by": ["company","city","region","title","industry","tenure"], "limit": 20}
                          → counts and % shares over ALL matches (free). Use this for every market report or "who/where" question.
- POST /companies/count   {"filters": {...}}
- POST /companies/search  {"filters": {...}, "limit": 10}
- GET  /projects?q=dairy&mine=true&monitored=true  → my projects (all optional: q = name contains, mine = ones I created/starred/get alerts for,
                          monitored = monitoring switched on). Each shows monitoring_enabled, moves_30d (backfills detected) and counts.
- POST /projects          {"name": "..."}  → empty project; or {"name": "...", "filters": {...}} → import every match into a new project
- POST /projects/{id}/people     {"filters": {...}}  → import matches into an existing project (skips people already there)
- POST /projects/{id}/companies  {"companies": ["acme.com", "Globex", "https://www.linkedin.com/company/initech"]}
- GET  /imports/{jobId}   → progress of an import (poll every ~10s until done)
- GET  /projects/{id}/changes?days=30  → monitored job moves in a project (backfill opportunities): who left which company and when (left_on),
                          every role they held there with the description THEY wrote, where they went, and the hiring managers already mapped there (free).
                          Each move has a verdict: departed (live backfill), internal_move (new role at the same company — old seat may need filling),
                          unknown (check first). Moves where they never left or left 90+ days earlier are hidden; add &include=all to see them.
- GET  /companies/people?company={ref}&expect={company name}&keywords=a,b,c&senior=true&projectId={id}&limit=25&offset=0
                          → people currently at a company: title, seniority, time in role, their own role description, LinkedIn; flags anyone already
                          in the project (free; up to 25 per call, page with offset). company = the "ref" from /changes, a LinkedIn company URL, domain or name.
                          Always pass expect = the company name you mean: if the result has a "warning", it searched a different company — retry with the
                          name or LinkedIn URL. People with left_per_monitoring have already left (the listing lags) — never suggest them as contacts.

People filters (all optional, fields AND together):
titles [], titleScope "current"|"primary"|"past"|"either"; locations [] (full country names like "United Kingdom" are exact and fastest; cities/regions also work);
headlineKeywords [], summaryKeywords [], roleKeywords [] (role descriptions, roleScope "current"); skills []; industries [] (EXACT industry names — "Education" excludes "Higher Education"); schools []; degrees [];
languages [] (spoken languages by English name, e.g. "German") with optional languageLevel "ELEMENTARY"|"LIMITED_WORKING"|"PROFESSIONAL_WORKING"|"FULL_PROFESSIONAL"|"NATIVE_OR_BILINGUAL" (minimum; ~1 in 10 profiles list languages); certifications [] (certificate name or issuer, e.g. "PRINCE2", "AWS Certified");
companies [] (names, domains or LinkedIn company URLs; companyScope "current"|"past"|"either"); tenureMin/tenureMax (years in role, decimals ok); experienceMin/experienceMax;
anywhereKeywords [] — slowest and least precise, use only if I ask for "anywhere in the profile".
keywordGroups [ {"terms": [], "exclude": [], "fields": [...]} ] — ANY term in ANY of the group's fields ("headline", "summary", "skills", "role_current", "role_past"); groups AND together. Use one group per concept, synonyms in the same group: (C# OR .NET) AND JavaScript in headline or summary = two groups with fields ["headline","summary"].
Title syntax: plain words = all words in any order; "quoted" = exact phrase; =Title = the whole title exactly. Matching is word-start (rust ≠ trusted).
Personas: {"orGroups": [ {filters}, {filters} ]} unions several filter sets — every group must keep the location and a titles anchor.

Company filters: industries [], locations [], nameKeywords [], descriptionKeywords [], keywords [], technologies [], headcountMin/Max, foundedAfter/Before, companiesList [].

Finding the project: when I name one, call GET /projects?q=<words from the name>; if more than one matches, show me the matches (name, moves_30d) and ask which.
If I don't name one, list GET /projects?monitored=true and suggest the ones with the most recent moves. Never guess a project id.
Backfills — finding who to call when someone leaves: GET /projects/{id}/changes, then for each move read what the leaver actually did (their role descriptions, not just the title).
Call /companies/people with company = that move's company.ref, projectId, and 3–8 specific keywords from their description (products, processes, systems, sites — not generic words like "engineer"),
once with senior=true and once without. Judge who most likely managed them or owns that team from titles AND descriptions (a generic "Engineering Manager" whose description matches is a stronger lead
than a better title that doesn't). Give me a short ranked list per move with your reasoning, and say which ones are already in my project. Draft outreach only when I ask.

Rules: only create projects or import when I ask. Counts and searches are free; imports count against my plan's per-project cap. Never show my API key in your replies.`;

// Starter's free month of Claude, as the API reports it: not started
// (offer), running (until), or ended.
export interface ConnectorMonth {
  connectorUntil: string | null;
  connectorMonth: boolean;
  connectorOffer: boolean;
  connectorHolder: boolean;
  connectorHolderEmail: string | null;
}

// The "Start my month of Claude" card. Shared by Settings → Integrations
// and Settings → Subscription; the account holder presses it, a team
// member is told who can.
export const ConnectorMonthCard: React.FC<{ token: string | null; info: ConnectorMonth; onStarted: () => void; compact?: boolean }> = ({ token, info, onStarted, compact }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = async () => {
    setBusy(true); setError(null);
    try {
      const r = await fetch('/api/api-keys/connector-month/start', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      const d = await r.json();
      if (r.ok) { tourEvent('connector-month-started'); onStarted(); } else setError(d.error || 'Could not start your month');
    } catch { setError('Could not start your month'); }
    setBusy(false);
  };
  if (!info.connectorOffer) return null;
  return (
    <div data-tour={compact ? undefined : 'connector-month-card'} className={`bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-lg ${compact ? 'p-4' : 'p-5'}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-semibold text-gray-900">Try Claude free for a month on your Starter plan</h4>
          <p className="text-sm text-gray-700 mt-1">
            Ask Claude in plain English (“how many DevOps engineers are there in the UK, and who employs most of them?”), get real numbers from the whole database, and say “add them to a project” to have it built for you.
            Claude is part of the Growth plan; the 30 days start when you press the button, not before, and cover everyone on your team (each person connects their own Claude with their own key). Afterwards you stay on Starter and Claude stops, unless you choose to upgrade. Nothing to cancel.
          </p>
          {!info.connectorHolder && (
            <p className="text-xs text-gray-600 mt-2">Only the account holder{info.connectorHolderEmail ? ` (${info.connectorHolderEmail})` : ''} can start it. Once it’s started, you create your own key here and connect your own Claude; what it builds goes into your account.</p>
          )}
          {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        </div>
        {info.connectorHolder && (
          <button className="shrink-0 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
            disabled={busy} onClick={start}>
            {busy ? 'Starting…' : 'Start my month of Claude'}
          </button>
        )}
      </div>
    </div>
  );
};

const ApiKeysPanel: React.FC<{ token: string | null }> = ({ token }) => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  // Starter's month of Claude: the end date, and whether it's still running.
  const [connectorUntil, setConnectorUntil] = useState<string | null>(null);
  const [connectorMonth, setConnectorMonth] = useState(false);
  const [offer, setOffer] = useState<ConnectorMonth | null>(null);
  const [justStarted, setJustStarted] = useState(false);
  const [plan, setPlan] = useState('');
  const [newName, setNewName] = useState('Claude');
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const base = window.location.origin;

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/api-keys', { headers: headers() });
      const d = await r.json();
      if (r.ok) {
        setKeys(d.keys || []); setAllowed(!!d.apiAllowed); setPlan(d.plan || ''); setConnectorUntil(d.connectorUntil || null); setConnectorMonth(!!d.connectorMonth);
        setOffer({ connectorUntil: d.connectorUntil || null, connectorMonth: !!d.connectorMonth, connectorOffer: !!d.connectorOffer, connectorHolder: !!d.connectorHolder, connectorHolderEmail: d.connectorHolderEmail || null });
      }
    } catch { /* leave as is */ }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true); setError(null);
    try {
      const r = await fetch('/api/api-keys', { method: 'POST', headers: headers(), body: JSON.stringify({ name: newName }) });
      const d = await r.json();
      if (r.ok) { setFreshKey(d.key); await load(); tourEvent('api-key-created'); } else setError(d.error || 'Could not create key');
    } catch { setError('Could not create key'); }
    setBusy(false);
  };

  const revoke = async (id: number) => {
    if (!window.confirm('Revoke this key? Anything using it (e.g. Claude) stops working immediately.')) return;
    await fetch(`/api/api-keys/${id}`, { method: 'DELETE', headers: headers() });
    if (freshKey) setFreshKey(null);
    load();
  };

  const copy = async (text: string, what: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(what); setTimeout(() => setCopied(null), 2000); }
    catch { /* clipboard blocked — the text is selectable */ }
  };

  if (allowed === null) return null;

  return (
    <div data-tour="claude-panel" className="card p-5 mb-6">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Claude &amp; API access</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Create a key and add MarketMapr to Claude as a connector: every chat can then count and search 431 million people and 49 million companies, and build projects for you. Keys act as your account — keep them private.
          </p>
        </div>
      </div>

      {!allowed ? (
        offer && offer.connectorOffer ? (
          <ConnectorMonthCard token={token} info={offer} onStarted={() => { setJustStarted(true); load(); }} />
        ) : (
          <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
            {connectorUntil
              ? <>Your month of Claude on Starter ended on {new Date(connectorUntil).toLocaleDateString('en-GB')}. It's included on Growth and Scale — <a href="/settings?tab=subscription" className="text-blue-600 hover:underline">upgrade</a> and your keys work again straight away.</>
              : <>API access is included on the Growth and Scale plans{plan ? ` (you're on ${plan})` : ''}.</>}
          </p>
        )
      ) : (
        <>
          {connectorMonth && connectorUntil && (
            <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              {justStarted ? <><strong>Your month of Claude has started.</strong> It runs</> : <>Claude is included on your Starter plan</>} until <strong>{new Date(connectorUntil).toLocaleDateString('en-GB')}</strong>{justStarted ? ' — create a key below and follow the three steps.' : ''}. On Growth and Scale it's part of the plan — <a href="/settings?tab=subscription" className="underline font-medium">see the plans</a>.
            </p>
          )}
          {freshKey && (
            <div data-tour="api-key-fresh" className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm font-medium text-emerald-900">Your new key — copy it now, it won't be shown again.</p>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 text-xs bg-white border border-emerald-200 rounded px-2 py-1.5 break-all select-all">{freshKey}</code>
                <button className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700" onClick={() => copy(freshKey, 'key')}>
                  {copied === 'key' ? 'Copied' : 'Copy key'}
                </button>
              </div>
              <div data-tour="connector-url" className="mt-3 pt-3 border-t border-emerald-200">
                <p className="text-sm font-medium text-emerald-900">Connect Claude — add this once, use it in every chat</p>
                <div className="mt-1.5 flex items-center gap-2">
                  <code className="flex-1 text-xs bg-white border border-emerald-200 rounded px-2 py-1.5 break-all select-all">{`${base}/api/mcp/${freshKey}`}</code>
                  <button className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700" onClick={() => copy(`${base}/api/mcp/${freshKey}`, 'mcp')}>
                    {copied === 'mcp' ? 'Copied' : 'Copy connector URL'}
                  </button>
                </div>
                <ol className="mt-2 text-xs text-emerald-900 space-y-0.5 list-decimal list-inside">
                  <li>In Claude (claude.ai or the desktop app): Settings → Connectors → <b>Add custom connector</b>.</li>
                  <li>Name it <b>MarketMapr</b>, paste the URL above, leave authentication off, press Add.</li>
                  <li>In any chat, make sure MarketMapr is switched on in the tools menu, then just ask — “how many senior planners in Nottingham?”, “map the C# market in Leeds into a project”.</li>
                </ol>
                <p className="mt-1.5 text-[11px] text-emerald-800">
                  Claude Code instead: <code className="bg-white border border-emerald-200 rounded px-1">claude mcp add --transport http marketmapr {`${base}/api/mcp/${freshKey}`}</code>
                </p>
                <p className="mt-1.5 text-[11px] text-emerald-700">The URL contains your key — it acts as you. Share it with nobody; revoke the key here if it leaks.</p>
              </div>
              <button className="mt-3 px-3 py-1.5 text-xs border border-emerald-300 text-emerald-800 rounded hover:bg-emerald-100"
                onClick={() => copy(claudePrompt(base, freshKey), 'prompt')}>
                {copied === 'prompt' ? 'Copied' : 'Other tools: copy the API setup prompt (key included)'}
              </button>
            </div>
          )}

          <div data-tour="api-key-create" className="flex items-center gap-2 mb-3">
            <input className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-48" value={newName}
              onChange={(e) => setNewName(e.target.value)} placeholder="Key name" maxLength={60} />
            <button className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              disabled={busy} onClick={create}>
              {busy ? 'Creating…' : 'Create API key'}
            </button>
            {error && <span className="text-xs text-red-600">{error}</span>}
          </div>

          {keys.length > 0 && (
            <table className="w-full text-sm mb-3">
              <thead>
                <tr className="text-left text-[11px] text-gray-500 uppercase tracking-wide">
                  <th className="py-1.5">Name</th><th>Key</th><th>Created</th><th>Last used</th><th />
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} className="border-t border-gray-100">
                    <td className="py-1.5">{k.name}</td>
                    <td><code className="text-xs text-gray-500">{k.key_prefix}…</code></td>
                    <td className="text-gray-500">{new Date(k.created_at).toLocaleDateString('en-GB')}</td>
                    <td className="text-gray-500">{k.last_used_at ? new Date(k.last_used_at).toLocaleDateString('en-GB') : 'Never'}</td>
                    <td className="text-right"><button className="text-xs text-red-600 hover:underline" onClick={() => revoke(k.id)}>Revoke</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <details className="text-xs text-gray-500">
            <summary className="cursor-pointer hover:text-gray-700">How to use it with Claude</summary>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Create a key above and copy the setup prompt (it includes your key).</li>
              <li>In Claude, make sure code execution is on and <code>{window.location.host}</code> is allowed for network access.</li>
              <li>Paste the prompt, then ask things like "how many DevOps engineers are there in the UK?" or "add these companies to a new project and find their heads of engineering".</li>
            </ol>
            <button className="mt-2 underline" onClick={() => copy(claudePrompt(base, '<your key>'), 'template')}>
              {copied === 'template' ? 'Copied' : 'Copy the prompt without a key'}
            </button>
          </details>
        </>
      )}
    </div>
  );
};

export default ApiKeysPanel;
